/**
 * Sync.
 *
 * The rules, in order of importance:
 *   1. A local transaction is never discarded. Failure leaves it queued.
 *   2. Conflicts resolve deterministically by `updatedAt`, using the same
 *      function on both sides, so the phone and the Worker always agree.
 *   3. Sync is optional. With it switched off, everything above is moot and the
 *      app carries on exactly as before.
 */
import { resolveConflict, type Transaction } from '@wis/shared';
import { db, readMeta, readSettings, writeMeta, type WhatISpentDatabase } from '../db/database.js';
import { saveTransaction } from '../db/repository.js';
import { readSessionId } from './account.js';

export type SyncStatus = 'idle' | 'syncing' | 'offline' | 'error' | 'disabled';

export interface SyncState {
  status: SyncStatus;
  pendingCount: number;
  failedCount: number;
  lastSyncedAt: string | null;
  lastError: string | null;
}

const CURSOR_KEY = 'sync.cursor';
const LAST_SYNCED_KEY = 'sync.lastSyncedAt';
const CLIENT_ID_KEY = 'sync.clientId';
const LAST_ERROR_KEY = 'sync.lastError';

/** Give up on an item after this many tries, but keep it queued and visible. */
export const MAX_ATTEMPTS = 5;

export class SyncError extends Error {
  constructor(
    message: string,
    readonly kind: 'network' | 'auth' | 'server' | 'validation',
  ) {
    super(message);
    this.name = 'SyncError';
  }
}

async function clientId(database: WhatISpentDatabase): Promise<string> {
  const existing = await readMeta<string>(CLIENT_ID_KEY, database);
  if (existing) return existing;
  const created = crypto.randomUUID();
  await writeMeta(CLIENT_ID_KEY, created, database);
  return created;
}

function readCsrfToken(): string {
  const match = /(?:^|;\s*)wis_csrf=([^;]+)/.exec(document.cookie);
  return match?.[1] ?? '';
}

async function call(
  baseUrl: string,
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const url = `${baseUrl.replace(/\/$/, '')}${path}`;
  // The session travels as a bearer token because the Worker is on a different
  // site from the app and Safari will not return its cookie. The cookie is
  // still sent when there is one, which is what happens in development.
  const sessionId = await readSessionId();
  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { Authorization: `Bearer ${sessionId}` } : {}),
        ...(init.method && init.method !== 'GET' ? { 'X-CSRF-Token': readCsrfToken() } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new SyncError('No connection to the sync server.', 'network');
  }

  if (response.status === 401 || response.status === 403) {
    throw new SyncError('Sign in again in Settings to keep syncing.', 'auth');
  }
  if (response.status === 422) {
    throw new SyncError('The server rejected some records.', 'validation');
  }
  if (!response.ok) {
    throw new SyncError(`Sync server returned ${response.status}.`, 'server');
  }
  return response;
}

export async function readSyncState(database: WhatISpentDatabase = db): Promise<SyncState> {
  const settings = await readSettings(database);
  const queue = await database.syncQueue.toArray();
  const failedCount = queue.filter((item) => item.attempts >= MAX_ATTEMPTS).length;

  const lastError = (await readMeta<string>(LAST_ERROR_KEY, database)) ?? null;

  return {
    status: settings.syncEnabled
      ? navigator.onLine
        ? lastError
          ? 'error'
          : 'idle'
        : 'offline'
      : 'disabled',
    pendingCount: queue.length,
    failedCount,
    lastSyncedAt: (await readMeta<string>(LAST_SYNCED_KEY, database)) ?? null,
    lastError,
  };
}

export interface SyncResult {
  pushed: number;
  pulled: number;
  conflicts: number;
  remaining: number;
}

/**
 * Push everything queued, then pull everything changed.
 *
 * Push first: if a record exists in both places, the server should already have
 * this device's version before we ask what it thinks the truth is.
 */
export async function runSync(database: WhatISpentDatabase = db): Promise<SyncResult> {
  const settings = await readSettings(database);
  if (!settings.syncEnabled || !settings.apiBaseUrl) {
    throw new SyncError('Cloud sync is switched off.', 'validation');
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new SyncError('This device is offline. Your changes are saved and queued.', 'network');
  }

  let pushed: number;
  let pull: PullResult;
  try {
    pushed = await pushQueue(settings.apiBaseUrl, database);
    pull = await pullChanges(settings.apiBaseUrl, database);
  } catch (failure) {
    /**
     * Remember why it failed.
     *
     * Most synchronisations now happen without being asked -- on opening the
     * app, and on returning to it -- so a failure has no screen to appear on.
     * Recording it here is what lets the app say "signed out" instead of just
     * showing an empty list and leaving the person to wonder where their
     * payments went, which is exactly how this went wrong once already.
     */
    await writeMeta(
      LAST_ERROR_KEY,
      failure instanceof Error ? failure.message : 'Sync failed.',
      database,
    );
    throw failure;
  }

  await writeMeta(LAST_ERROR_KEY, null, database);
  await writeMeta(LAST_SYNCED_KEY, new Date().toISOString(), database);

  return {
    pushed,
    pulled: pull.applied,
    conflicts: pull.conflicts,
    remaining: await database.syncQueue.count(),
  };
}

/**
 * Drain the queue.
 *
 * On failure the queue is left intact with an attempt count and the error
 * message, which is what the "Retry synchronisation" button acts on. Nothing
 * is dropped, ever -- a record that cannot sync is still a record the user has.
 */
export async function pushQueue(
  baseUrl: string,
  database: WhatISpentDatabase = db,
): Promise<number> {
  const queue = await database.syncQueue.orderBy('id').toArray();
  if (queue.length === 0) return 0;

  // One entry per transaction: only the latest state needs to travel.
  const byTransaction = new Map<string, number[]>();
  for (const item of queue) {
    const ids = byTransaction.get(item.transactionId) ?? [];
    if (item.id !== undefined) ids.push(item.id);
    byTransaction.set(item.transactionId, ids);
  }

  const transactions: Transaction[] = [];
  const missing: number[] = [];

  for (const [transactionId, queueIds] of byTransaction) {
    const transaction = await database.transactions.get(transactionId);
    if (transaction) {
      transactions.push(transaction);
    } else {
      // The record is gone locally, so there is nothing left to push.
      missing.push(...queueIds);
    }
  }

  if (missing.length > 0) await database.syncQueue.bulkDelete(missing);
  if (transactions.length === 0) return 0;

  const batchSize = 200;
  let sent = 0;

  for (let index = 0; index < transactions.length; index += batchSize) {
    const batch = transactions.slice(index, index + batchSize);
    try {
      await call(baseUrl, '/api/sync/push', {
        method: 'POST',
        body: JSON.stringify({ transactions: batch, clientId: await clientId(database) }),
      });
    } catch (error) {
      await recordFailure(
        database,
        batch.map((transaction) => transaction.id),
        error instanceof Error ? error.message : 'Sync failed',
      );
      throw error;
    }

    const acceptedIds = batch.flatMap(
      (transaction) => byTransaction.get(transaction.id) ?? [],
    );
    await database.syncQueue.bulkDelete(acceptedIds);
    sent += batch.length;
  }

  return sent;
}

async function recordFailure(
  database: WhatISpentDatabase,
  transactionIds: readonly string[],
  message: string,
): Promise<void> {
  const now = new Date().toISOString();
  const items = await database.syncQueue.toArray();
  const affected = items.filter((item) => transactionIds.includes(item.transactionId));
  await database.syncQueue.bulkPut(
    affected.map((item) => ({
      ...item,
      attempts: item.attempts + 1,
      lastAttemptAt: now,
      lastError: message.slice(0, 200),
    })),
  );
}

export interface PullResult {
  applied: number;
  conflicts: number;
}

/** Pull, then merge with the same conflict rule the server uses. */
export async function pullChanges(
  baseUrl: string,
  database: WhatISpentDatabase = db,
): Promise<PullResult> {
  const cursor = await readMeta<string>(CURSOR_KEY, database);
  const query = cursor ? `?since=${encodeURIComponent(cursor)}` : '';
  const response = await call(baseUrl, `/api/sync/changes${query}`);
  const body = (await response.json()) as {
    transactions: Transaction[];
    cursor: string | null;
  };

  let applied = 0;
  let conflicts = 0;

  for (const remote of body.transactions) {
    const local = await database.transactions.get(remote.id);

    if (!local) {
      await saveTransaction(remote, { database, fromServer: true });
      applied += 1;
      continue;
    }

    if (local.updatedAt === remote.updatedAt) continue;

    const winner = resolveConflict(local, remote);
    conflicts += 1;

    if (winner !== local) {
      await saveTransaction(winner, { database, fromServer: true });
      applied += 1;
    }
    // If the local copy won, it is already queued; nothing to do here.
  }

  if (body.cursor) await writeMeta(CURSOR_KEY, body.cursor, database);

  return { applied, conflicts };
}

/**
 * Retry everything, including entries that have used up their attempts.
 * The counters reset, because the user asking for a retry is new information.
 */
export async function retryFailed(database: WhatISpentDatabase = db): Promise<SyncResult> {
  const items = await database.syncQueue.toArray();
  await database.syncQueue.bulkPut(
    items.map((item) => {
      const { lastError: _dropped, ...rest } = item;
      return { ...rest, attempts: 0 };
    }),
  );
  return runSync(database);
}

/** Per-transaction badge state for the Transactions list. */
export async function syncStateForTransaction(
  transactionId: string,
  database: WhatISpentDatabase = db,
): Promise<'local' | 'pending' | 'synced' | 'failed'> {
  const settings = await readSettings(database);
  if (!settings.syncEnabled) return 'local';

  const queued = await database.syncQueue.where('transactionId').equals(transactionId).toArray();
  if (queued.length === 0) return 'synced';
  return queued.some((item) => item.attempts >= MAX_ATTEMPTS) ? 'failed' : 'pending';
}
